<template>
  <section class="pu-modal">
    <div
      v-show="showTrigger"
      class="modal-trigger-wrapper"
      @click.stop.prevent.capture="openModal"
    >
      <slot name="modal-trigger"> Open modal </slot>
    </div>

    <client-only>
      <Teleport
        v-if="showModal || showModalState"
        :disabled="!usePortal"
        to="body"
      >
        <transition name="modal" tag="div">
          <div
            v-if="showModal || showModalState"
            class="modal-container"
            :class="[modalClass, { desktop: $isDesktop }]"
          >
            <div class="modal-header">
              <slot name="header">
                <a
                  class="close-button"
                  data-test="modal-close-button"
                  @click.prevent.stop="closeModal"
                >
                  <img
                    loading="lazy"
                    src="@/assets/images/x.svg"
                    width="14"
                    height="14"
                  />
                </a>
              </slot>
            </div>

            <div class="modal-body">
              <slot :close-modal="closeModal" />
            </div>

            <div class="modal-footer">
              <slot name="footer" :close-modal="closeModal" />
            </div>
          </div>
        </transition>

        <div class="pagemask" @click.prevent="closeModal" />
      </Teleport>
    </client-only>
  </section>
</template>

<script setup lang="ts">
const emit = defineEmits([
  'change-show-modal-state',
  'closeModal',
  'closePUModal',
  'openModal',
  'openPUModal',
])

const { finish } = useLoadingIndicator()

const props = withDefaults(
  defineProps<{
    showModal?: boolean
    showTrigger?: boolean
    usePortal?: boolean
    modalClass?: string
  }>(),
  {
    showModal: false,
    showTrigger: true,
    usePortal: false,
  },
)

const showModalState = ref(false)

function setModalStateOpen() {
  showModalState.value = true
  emit('change-show-modal-state', true)
}

function setModalStateClosed() {
  showModalState.value = false
  emit('change-show-modal-state', false)
}

watch(
  () => props.showModal,
  (newValue: boolean) => {
    newValue
      ? document.body.setAttribute('data-noscroll', 'true')
      : document.body.removeAttribute('data-noscroll')
  },
)

function openModal() {
  setModalStateOpen()
  emit('openModal')
  finish()
}

function closeModal() {
  setModalStateClosed()
  emit('closeModal')
  finish()
}

onBeforeUnmount(() => {
  if (document && document.body) {
    document.body.removeAttribute('data-noscroll')
  }
})
</script>

<style scoped>
.pagemask {
  display: flex;
  justify-content: center;
}

.modal-container {
  background-color: #fff;
  border-radius: 0.6em;
  bottom: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  display: flex;
  flex-direction: column;
  height: auto;
  left: 3vw;
  margin: 0 auto;
  max-height: calc(100% - 6vw);
  min-height: 10em;
  min-width: 15em;
  overflow: hidden;
  position: fixed;
  right: 3vw;
  top: 3vw;
  transition: all 0.2s ease;
  width: 94vw;
  z-index: 98;
}

.modal-header,
.modal-footer {
  height: auto;
  position: relative;
  z-index: 99;
}

.modal-header {
  height: auto;
}

.modal-body {
  height: 100%;
  overflow-y: auto;
}

:deep(.close-button) {
  background: var(--colour-page-background);
  border-radius: 1.75em;
  border: none;
  box-shadow: none;
  display: block;
  height: 6em;
  line-height: 0;
  opacity: 1;
  padding: 0;
  position: absolute;
  right: -3.2em;
  top: -3.2em;
  width: 6em;
}

:deep(.close-button:hover, .close-button:focus-visible, .close-button:active) {
  background: #a0abb8;
  box-shadow: none;
  opacity: 1;
  transform: none;
  outline: none;
}

:deep(.close-button img) {
  bottom: 1.1em;
  height: auto;
  left: 1.1em;
  position: absolute;
  width: 0.85em;
}

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(0.8);
  transform: scale(0.8);
}

/* desktop & tablet */
.modal-container.desktop {
  max-width: 26em;
  font-size: initial;
}
</style>
